import React, { useState, useEffect } from 'react';
import { SPARK_URL } from './apiConfig';

// ============================================
// DatePlannerView — the new Dates tab
// ============================================
// Three ways to plan a date:
//   1. Maia suggests — she knows you both, let her plan it
//   2. AI search — tell the AI what you want, it finds options
//   3. I'll plan it — pick everything yourself
//
// Below the planner: existing date bookings (active + archived)
// ============================================

const API = SPARK_URL;

const PLANNING_MODES = [
  {
    id: 'maia',
    icon: '💜',
    title: 'Let Maia plan it',
    desc: 'She knows you both — let her surprise you',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    id: 'search',
    icon: '🔍',
    title: 'Help me search',
    desc: 'Tell me your vibe and budget — I\'ll find options',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
  },
  {
    id: 'manual',
    icon: '✨',
    title: 'I\'ll plan it',
    desc: 'You pick the venue, time, and everything',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
  },
];

const VIBES = ['Casual & chill', 'Romantic', 'Adventurous', 'Fun & playful', 'Classy', 'Outdoors', 'Cosy'];
const CUISINES = ['Any', 'Italian', 'Japanese', 'Mexican', 'Thai', 'Indian', 'Cafe/Brunch', 'Pub food', 'Fine dining', 'Street food'];
const BUDGETS = [
  { id: 'free', label: 'Free', desc: 'Parks, walks, sunsets' },
  { id: 'cheap', label: 'Under $30', desc: 'Coffee, casual eats' },
  { id: 'mid', label: '$30–$80', desc: 'Nice dinner, activity' },
  { id: 'splurge', label: '$80+', desc: 'Something special' },
];

export default function DatePlannerView({
  dateBookings, archivedDateBookings, showArchivedDates, setShowArchivedDates,
  matches, profile, user, error,
  onAcceptDate, onDeclineDate, onCancelDate, onConfirmBooking,
  onArchiveDateBooking, onUnarchiveDateBooking,
  onLoadAllDateBookings,
  setSelectedMatch, setShowDateModal, setCounterProposalBooking,
  // New: DatesView component for existing bookings
  DatesViewComponent,
}) {
  const [mode, setMode] = useState(null);        // null | 'maia' | 'search' | 'manual'
  const [step, setStep] = useState('select');     // select | match | criteria | results | confirm
  const [selectedMatchId, setSelectedMatchId] = useState(null);

  // Search criteria
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('Any');
  const [selectedBudget, setSelectedBudget] = useState('mid');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  // Maia suggestion
  const [maiaLoading, setMaiaLoading] = useState(false);
  const [maiaSuggestion, setMaiaSuggestion] = useState(null);

  const token = localStorage.getItem('accessToken');

  const selectedMatch = matches?.find(m => m.matchId === selectedMatchId);

  // Reset when mode changes
  useEffect(() => {
    setStep(mode ? 'match' : 'select');
    setSelectedMatchId(null);
    setSearchResults(null);
    setMaiaSuggestion(null);
  }, [mode]);

  // ── Ask Maia for a suggestion ──────────────────────────────
  const askMaia = async () => {
    if (!selectedMatchId) return;
    setMaiaLoading(true);
    try {
      const res = await fetch(`${API}/dates/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ matchId: selectedMatchId }),
      });
      const data = await res.json();
      if (data.success) {
        setMaiaSuggestion(data.data);
        setStep('results');
      } else {
        setMaiaSuggestion({ error: data.message || 'Maia couldn\'t think of something right now. Try again?' });
        setStep('results');
      }
    } catch {
      setMaiaSuggestion({ error: 'Something went wrong. Try again?' });
      setStep('results');
    }
    setMaiaLoading(false);
  };

  // ── AI search with criteria ────────────────────────────────
  const aiSearch = async () => {
    if (!selectedMatchId) return;
    setSearchLoading(true);
    try {
      const res = await fetch(`${API}/dates/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          matchId: selectedMatchId,
          vibes: selectedVibes,
          cuisine: selectedCuisine,
          budget: selectedBudget,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSearchResults(data.data);
      } else {
        setSearchResults({ error: data.message || 'Search didn\'t find anything. Try different criteria?' });
      }
      setStep('results');
    } catch {
      setSearchResults({ error: 'Something went wrong. Try again?' });
      setStep('results');
    }
    setSearchLoading(false);
  };

  // ── Styles ─────────────────────────────────────────────────
  const s = {
    container: { padding: '16px 16px 100px', maxWidth: 600, margin: '0 auto' },
    heading: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' },
    subtext: { fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 },
    modeCard: (m, isActive) => ({
      background: isActive ? m.gradient : 'var(--surface)',
      border: isActive ? 'none' : '1px solid var(--border)',
      borderRadius: 16,
      padding: '16px 18px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      transform: isActive ? 'scale(1.02)' : 'scale(1)',
    }),
    modeTitle: (isActive) => ({
      fontSize: 15, fontWeight: 700,
      color: isActive ? '#fff' : 'var(--text-primary)',
      margin: '6px 0 2px',
    }),
    modeDesc: (isActive) => ({
      fontSize: 12,
      color: isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)',
      margin: 0,
    }),
    matchCard: (isSelected) => ({
      display: 'flex', alignItems: 'center', gap: 12,
      background: isSelected ? 'var(--primary)' : 'var(--surface)',
      border: isSelected ? 'none' : '1px solid var(--border)',
      borderRadius: 14, padding: '12px 14px',
      cursor: 'pointer', transition: 'all 0.2s',
    }),
    matchName: (isSelected) => ({
      fontSize: 15, fontWeight: 600,
      color: isSelected ? '#fff' : 'var(--text-primary)',
    }),
    matchSub: (isSelected) => ({
      fontSize: 12,
      color: isSelected ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)',
    }),
    btn: (bg) => ({
      width: '100%', padding: '14px', border: 'none', borderRadius: 12,
      fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#fff',
      background: bg || 'var(--primary)',
      transition: 'all 0.2s',
    }),
    btnOutline: {
      width: '100%', padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 600,
      cursor: 'pointer', color: 'var(--text-secondary)',
      background: 'transparent', border: '1px solid var(--border)',
    },
    chip: (isActive) => ({
      padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
      cursor: 'pointer', border: 'none', transition: 'all 0.15s',
      background: isActive ? 'var(--primary)' : 'var(--surface-alt)',
      color: isActive ? '#fff' : 'var(--text-primary)',
    }),
    section: { marginBottom: 20 },
    sectionLabel: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px' },
    resultCard: {
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16,
      padding: 18, marginBottom: 10,
    },
    backBtn: {
      background: 'none', border: 'none', color: 'var(--primary)',
      fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 0', marginBottom: 12,
    },
  };

  // ── Count active dates for the section header ──────────────
  const activeDateCount = (dateBookings || []).filter(b =>
    ['pending', 'accepted', 'suggest_changes'].includes(b.status)
  ).length;

  return (
    <div style={s.container}>

      {/* ═══ PLANNING SECTION ═══ */}
      {step === 'select' && (
        <>
          <h2 style={s.heading}>Plan a date</h2>
          <p style={s.subtext}>Three ways to make it happen</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {PLANNING_MODES.map(m => (
              <div key={m.id} style={s.modeCard(m, mode === m.id)}
                onClick={() => setMode(mode === m.id ? null : m.id)}>
                <span style={{ fontSize: 24 }}>{m.icon}</span>
                <div style={s.modeTitle(mode === m.id)}>{m.title}</div>
                <p style={s.modeDesc(mode === m.id)}>{m.desc}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ═══ MATCH SELECTION ═══ */}
      {step === 'match' && (
        <>
          <button style={s.backBtn} onClick={() => { setMode(null); setStep('select'); }}>
            ← Back
          </button>
          <h2 style={s.heading}>Who's the date with?</h2>
          <p style={s.subtext}>Pick a match to plan a date with</p>

          {(!matches || matches.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              <span style={{ fontSize: 40, display: 'block', marginBottom: 10 }}>💔</span>
              <p style={{ fontSize: 14 }}>No matches yet. Keep swiping!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {matches.map(m => (
                <div key={m.matchId} style={s.matchCard(selectedMatchId === m.matchId)}
                  onClick={() => setSelectedMatchId(m.matchId)}>
                  <img
                    src={m.otherUser?.photos?.[0]?.url || 'https://via.placeholder.com/44'}
                    alt={m.otherUser?.firstName}
                    style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={s.matchName(selectedMatchId === m.matchId)}>{m.otherUser?.firstName}</div>
                    <div style={s.matchSub(selectedMatchId === m.matchId)}>
                      {m.lastMessage ? m.lastMessage.substring(0, 40) + '...' : 'Start planning!'}
                    </div>
                  </div>
                  {selectedMatchId === m.matchId && (
                    <span style={{ fontSize: 18, color: '#fff' }}>✓</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedMatchId && (
            <button style={s.btn()} onClick={() => {
              if (mode === 'maia') { askMaia(); setStep('results'); }
              else if (mode === 'search') { setStep('criteria'); }
              else if (mode === 'manual') {
                const match = matches.find(m => m.matchId === selectedMatchId);
                if (match) { setSelectedMatch(match); setShowDateModal(true); }
                setMode(null); setStep('select');
              }
            }}>
              {mode === 'maia' ? '💜 Let Maia plan it' :
               mode === 'search' ? '🔍 Set my criteria' :
               '✨ Open date planner'}
            </button>
          )}
        </>
      )}

      {/* ═══ SEARCH CRITERIA ═══ */}
      {step === 'criteria' && (
        <>
          <button style={s.backBtn} onClick={() => setStep('match')}>← Back</button>
          <h2 style={s.heading}>What's the vibe?</h2>
          <p style={s.subtext}>
            Tell me what you're after and I'll find options
            {selectedMatch && <> for your date with {selectedMatch.otherUser?.firstName}</>}
          </p>

          <div style={s.section}>
            <div style={s.sectionLabel}>Vibe (pick as many as you like)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {VIBES.map(v => (
                <button key={v} style={s.chip(selectedVibes.includes(v))}
                  onClick={() => setSelectedVibes(prev =>
                    prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
                  )}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div style={s.section}>
            <div style={s.sectionLabel}>Cuisine / type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CUISINES.map(c => (
                <button key={c} style={s.chip(selectedCuisine === c)}
                  onClick={() => setSelectedCuisine(c)}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div style={s.section}>
            <div style={s.sectionLabel}>Budget per person</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {BUDGETS.map(b => (
                <div key={b.id}
                  style={{
                    ...s.matchCard(selectedBudget === b.id),
                    padding: '10px 14px',
                  }}
                  onClick={() => setSelectedBudget(b.id)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: selectedBudget === b.id ? '#fff' : 'var(--text-primary)' }}>
                      {b.label}
                    </div>
                    <div style={{ fontSize: 12, color: selectedBudget === b.id ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)' }}>
                      {b.desc}
                    </div>
                  </div>
                  {selectedBudget === b.id && <span style={{ color: '#fff' }}>✓</span>}
                </div>
              ))}
            </div>
          </div>

          <button style={s.btn('linear-gradient(135deg, #ec4899, #f43f5e)')}
            onClick={aiSearch} disabled={searchLoading}>
            {searchLoading ? '🔍 Searching...' : '🔍 Find date ideas'}
          </button>
        </>
      )}

      {/* ═══ RESULTS ═══ */}
      {step === 'results' && (
        <>
          <button style={s.backBtn} onClick={() => {
            setStep(mode === 'maia' ? 'match' : 'criteria');
            setSearchResults(null);
            setMaiaSuggestion(null);
          }}>← Back</button>

          {/* Maia suggestion */}
          {mode === 'maia' && (
            <>
              {maiaLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>💜</span>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                    Maia is thinking...
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    She's looking at what you both like and finding something perfect
                  </p>
                </div>
              ) : maiaSuggestion?.error ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{maiaSuggestion.error}</p>
                  <button style={s.btnOutline} onClick={() => { setStep('match'); setMaiaSuggestion(null); }}>
                    Try again
                  </button>
                </div>
              ) : maiaSuggestion ? (
                <>
                  <h2 style={s.heading}>Maia's suggestion</h2>
                  <p style={s.subtext}>Here's what I think you'd both love</p>

                  <div style={{
                    ...s.resultCard,
                    background: 'linear-gradient(135deg, var(--surface) 0%, var(--surface-alt) 100%)',
                    border: '2px solid var(--primary)',
                  }}>
                    {maiaSuggestion.venue && (
                      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                        📍 {maiaSuggestion.venue}
                      </div>
                    )}
                    {maiaSuggestion.address && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>{maiaSuggestion.address}</div>
                    )}
                    {maiaSuggestion.description && (
                      <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7, marginBottom: 12 }}>
                        {maiaSuggestion.description}
                      </div>
                    )}
                    {maiaSuggestion.whyThisWorks && (
                      <div style={{
                        background: 'rgba(99,102,241,0.08)', borderRadius: 10, padding: '10px 14px',
                        marginBottom: 12, borderLeft: '3px solid var(--primary)',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>Why this works for you two</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{maiaSuggestion.whyThisWorks}</div>
                      </div>
                    )}
                    {maiaSuggestion.estimatedCost && (
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>💰 {maiaSuggestion.estimatedCost}</div>
                    )}
                    {maiaSuggestion.suggestedTime && (
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>🕐 {maiaSuggestion.suggestedTime}</div>
                    )}
                    {maiaSuggestion.conversationStarter && (
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>💬 Ice-breaker: "{maiaSuggestion.conversationStarter}"</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...s.btn(), flex: 1 }} onClick={() => {
                      const match = matches.find(m => m.matchId === selectedMatchId);
                      if (match) {
                        setSelectedMatch(match);
                        setShowDateModal(true);
                      }
                      // Pre-fill from Maia's suggestion would happen via state
                    }}>
                      ✅ Love it — propose this date
                    </button>
                    <button style={{ ...s.btnOutline, flex: 1 }} onClick={askMaia}>
                      🔄 Try another
                    </button>
                  </div>
                </>
              ) : null}
            </>
          )}

          {/* AI search results */}
          {mode === 'search' && (
            <>
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>🔍</span>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Searching...</p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Finding {selectedVibes.length > 0 ? selectedVibes.join(' & ').toLowerCase() : 'great'} date ideas
                    {selectedBudget !== 'mid' && ' on your budget'}
                  </p>
                </div>
              ) : searchResults?.error ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{searchResults.error}</p>
                  <button style={s.btnOutline} onClick={() => setStep('criteria')}>
                    Adjust criteria
                  </button>
                </div>
              ) : searchResults?.options ? (
                <>
                  <h2 style={s.heading}>Date ideas</h2>
                  <p style={s.subtext}>{searchResults.options.length} option{searchResults.options.length !== 1 ? 's' : ''} found</p>

                  {searchResults.options.map((opt, i) => (
                    <div key={i} style={s.resultCard}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                        📍 {opt.venue || opt.name}
                      </div>
                      {opt.address && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{opt.address}</div>
                      )}
                      {opt.description && (
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: 8 }}>
                          {opt.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        {opt.vibe && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'rgba(99,102,241,0.1)', color: 'var(--primary)', fontWeight: 600 }}>{opt.vibe}</span>}
                        {opt.price && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'rgba(34,197,94,0.1)', color: 'var(--success)', fontWeight: 600 }}>{opt.price}</span>}
                        {opt.rating && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'rgba(251,191,36,0.1)', color: 'var(--warning)', fontWeight: 600 }}>⭐ {opt.rating}</span>}
                      </div>
                      <button style={{ ...s.btn(), fontSize: 13, padding: 10 }} onClick={() => {
                        const match = matches.find(m => m.matchId === selectedMatchId);
                        if (match) {
                          setSelectedMatch(match);
                          setShowDateModal(true);
                        }
                      }}>
                        Choose this — propose date
                      </button>
                    </div>
                  ))}

                  <button style={{ ...s.btnOutline, marginTop: 8 }} onClick={() => setStep('criteria')}>
                    ← Adjust criteria
                  </button>
                </>
              ) : null}
            </>
          )}
        </>
      )}

      {/* ═══ EXISTING DATES ═══ */}
      {step === 'select' && (
        <>
          <div style={{
            marginTop: 8, marginBottom: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <h2 style={{ ...s.heading, fontSize: 18 }}>
              Your dates {activeDateCount > 0 && <span style={{ fontSize: 14, color: 'var(--primary)' }}>({activeDateCount})</span>}
            </h2>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setShowArchivedDates(false)}
                style={{
                  padding: '6px 14px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: !showArchivedDates ? 'var(--primary)' : 'var(--surface-alt)',
                  color: !showArchivedDates ? '#fff' : 'var(--text-secondary)',
                }}>
                Active
              </button>
              <button
                onClick={() => setShowArchivedDates(true)}
                style={{
                  padding: '6px 14px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: showArchivedDates ? 'var(--primary)' : 'var(--surface-alt)',
                  color: showArchivedDates ? '#fff' : 'var(--text-secondary)',
                }}>
                Archived
              </button>
            </div>
          </div>

          {/* Render existing DatesView content inline */}
          {DatesViewComponent}
        </>
      )}
    </div>
  );
}
